<template>
  <AuthLayout :mode="mode">
    <Section
      heading="Welcome to the eTN <br /> Trading Network"
      description="In this part of our web site, Subscribers to the ASCDI Trading Network (eTN Subscribers) will have access to features and tools to help you in the buying and selling of equipment. Click on the links below (or to your left) to help you buy/sell the equipment."
      :mode="mode"
    />
    <div class="grid gap-6">
      <NumericCard :mode="mode" :title="'PowerSearch'" :number="1">
        <template #body>
          <p
            class="text-[14px] font-medium"
            :class="[mode === 'dark' ? 'text-dark-text' : 'text-black-60']"
          >
            Quickly find the equipment you need using our
            <br class="sm:hidden" />
            database. You can also save your search and schedule
            <br class="sm:hidden" />
            it to run automatically.
          </p>
          <div class="w-[180px] sm:w-full">
            <AppButton
              :isLink="true"
              :to="{ name: 'search' }"
              title="Start search"
              type="secondary"
            />
          </div>
        </template>
      </NumericCard>
      <NumericCard :mode="mode" :title="'Broadcast-Add Listings'" :number="2">
        <template #body>
          <p
            class="text-[14px] font-medium"
            :class="[mode === 'dark' ? 'text-dark-text' : 'text-black-60']"
          >
            Send equipment listings by email in a fixed format to buyers
            <br class="sm:hidden" />
            and sellers.This also uploads your list to the eTN database for
            <br class="sm:hidden" />
            use in PowerSearch.
          </p>
          <div class="w-[180px] sm:w-full">
            <AppButton
              :isLink="true"
              :to="{ name: 'add-listing' }"
              title="Send listing"
              type="secondary"
            />
          </div>
        </template>
      </NumericCard>
      <NumericCard
        :mode="mode"
        :title="'PowerCast - Free Format Listings'"
        :number="3"
      >
        <template #body>
          <p
            class="text-[14px] font-medium"
            :class="[mode === 'dark' ? 'text-dark-text' : 'text-black-60']"
          >
            Share listings via email in a free format. These will also
            <br class="sm:hidden" />
            be added to the eTN database for PowerSearch use. use in
            PowerSearch.
          </p>
          <div class="w-[180px] sm:w-full">
            <AppButton
              :isLink="true"
              :to="{ name: 'broadcast-email' }"
              title="Send email"
              type="secondary"
            />
          </div>
        </template>
      </NumericCard>
      <NumericCard :mode="mode" :title="'PowerMail'" :number="4">
        <template #body>
          <p
            class="text-[14px] font-medium"
            :class="[mode === 'dark' ? 'text-dark-text' : 'text-black-60']"
          >
            Email your offers or requests directly from your mail client
            <br class="sm:hidden" />
            (e.g., Outlook). No login needed to post or view daily listings.
          </p>
          <div class="w-[180px] sm:w-full">
            <AppButton
              :isLink="true"
              :to="{ name: 'broadcast-email' }"
              title="Send from email"
              type="secondary"
            />
          </div>
        </template>
      </NumericCard>
      <NumericCard :mode="mode" :title="'PowerLoad'" :number="5">
        <template #body>
          <p
            class="text-[14px] font-medium"
            :class="[mode === 'dark' ? 'text-dark-text' : 'text-black-60']"
          >
            Upload your equipment spreadsheet for sale. It will also
            <br class="sm:hidden" />
            be listed in eTN and appear in PowerSearch results.
          </p>
          <div class="w-[180px] sm:w-full">
            <AppButton
              :isLink="true"
              :to="{ name: 'upload' }"
              title="Send search"
              type="secondary"
            />
          </div>
        </template>
      </NumericCard>
    </div>
    <div class="mt-[60px] sm:mt-[40px]">
      <div class="mb-[48px] text-center sm:mb-8">
        <h2 class="text-[34px] font-semibold sm:text-[22px]">
          More Tools & Info
        </h2>
      </div>
      <div
        class="grid grid-cols-3 gap-y-6 sm:grid-cols-1 sm:divide-y sm:divide-border-10"
      >
        <ToolCard
          v-for="(tool, key) in tools"
          :key="key"
          :mode="mode"
          :tool="tool"
        />
      </div>
    </div>
  </AuthLayout>
</template>

<script>
import {
  FileCog,
  Landmark,
  MailSearch,
  Package,
  ShieldHalf,
  SlidersVertical,
  Users,
} from "lucide-vue-next";
import Section from "@C/Common/Section.vue";
import ToolCard from "@C/Common/ToolCard.vue";
import NumericCard from "@C/Common/NumericCard.vue";
import AuthLayout from "@A/AuthLayout.vue";
import AppButton from "@C/Common/AppButton.vue";

export default {
  name: "TradingNetwork",
  components: {
    AuthLayout,
    ToolCard,
    NumericCard,
    Users,
    Section,
    Package,
    SlidersVertical,
    FileCog,
    MailSearch,
    ShieldHalf,
    Landmark,
    AppButton,
  },
  props: {
    mode: String,
  },
  data() {
    return {
      tools: [
        {
          title: "PowerSpecs - Equipment Info",
          description:
            "View detailed technical specs <br /> for most IT equipment.",
          icon: Package,
          link: "equip-spec-sheets",
          linkText: "View Specs",
          isLast: false,
          isFirst: true,
        },
        {
          title: "Manage Listings",
          description: "Edit and update your own <br /> equipment listings.",
          icon: FileCog,
          link: "manage",
          linkText: "Edit listings",
          isLast: false,
          isFirst: false,
        },
        {
          title: "Edit Saved Searches",
          description: "Customize and adjust your <br /> saved search filters.",
          icon: SlidersVertical,
          link: "searches",
          linkText: "Edit filters",
          isLast: false,
          isFirst: false,
        },
        {
          title: "Find / Email a Dealer",
          description: "Customize and adjust your <br /> saved search filters.",
          icon: MailSearch,
          link: "member-directory",
          linkText: "Contact Dealer",
          isLast: false,
          isFirst: false,
        },
        {
          title: "Ethics Violators",
          description:
            "See companies that violated the <br /> ASCDI code of ethics.",
          icon: ShieldHalf,
          link: "violations",
          linkText: "View List",
          isLast: false,
          isFirst: false,
        },
        {
          title: "Ethics Non-participants",
          description:
            "View companies that declined to <br /> follow the ASCDI code of ethics.",
          icon: Users,
          link: "ethics-non-participants",
          linkText: "View List",
          isLast: false,
          isFirst: false,
        },
        {
          title: "Company Info & Users",
          description:
            "Edit your company contact info <br /> or manage users via the settings panel.",
          icon: Landmark,
          link: "settings",
          linkText: "Manage info",
          isLast: true,
          isFirst: false,
        },
      ],
    };
  },
};
</script>
